<template>
    <div class="marketing-plan-day-overview-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <div class="widget-title-container">
                    <j9-overview-svg />
                    <h3>{{day | momentDateMMMMDYYYY}}</h3>
                </div>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div v-if="isLoading">
                    <loading-screen-svg />
                </div>
                <div v-if="hasCampaignsScheduled" class="campaigns">
                    <p>These are all campaigns that are set for this day.</p>
                    <div v-for="campaign in campaigns" :key="campaign.id" class="campaign">
                        <app-button
                            v-if="!campaign.isSent"
                            size="medium"
                            class="delete-btn"
                            @click="showCampaignDeletePopUp(campaign)">
                            <s4-delete-svg />
                        </app-button>
                        <div class="title-row">
                            <div class="preview" @click="previewContent(campaign)" v-if="campaign.testType !== 'Content'">
                                <div class="preview-overlay"><eye-svg /></div>
                                <img :src="campaign.previewImageUrl | templatePreviewImagePlaceholder" />
                                <div class="version-bubble" v-if="campaign.winningVersion">
                                    <j16-a-test-svg v-if="campaign.winningVersion === 'A'"/>
                                    <k16-b-test-svg v-else/>
                                </div>
                            </div>
                            <div class="campaign-name-container">
                                <p class="campaign-name unsent-campaign" 
                                    v-if="!campaign.isSent"
                                    @click="openEditCampaignWidget(campaign)">
                                        <span>{{campaign.name}}</span> 
                                        <r7-edit-svg />
                                </p>
                                <p class="campaign-name" v-else>{{campaign.name}}</p>
                                <p class="campaign-subject" v-if="campaign.testType !== 'Subject'"><span class="heavy-weight">Subject Line:</span> {{campaign.subjectA}}</p>
                                <p v-if="campaign.hasAbTest && campaign.isSent" class="version-winner-text"><span class="red-text" v-if="campaign.winningVersion ==='A'">Version A </span><span v-else class="blue-text">Version B </span>won based off of <span class="heavy-weight">{{campaign.testMetric}}</span> after being sent to <span class="heavy-weight"> {{campaign.testedContacts | commaDelimited}} contacts</span></p>                            </div>
                        </div>
                        <div class="ab-test-row" v-if="campaign.hasAbTest && !campaign.isSent" :class="{'subject-test': campaign.testType === 'Subject' }">
                            <div class="test-content">
                                <j16-a-test-svg />
                                <div class="preview" 
                                    @click="previewContent(campaign,'A')"
                                    v-if="campaign.testType === 'Content'">
                                    <div class="preview-overlay"><eye-svg /></div>
                                    <img :src="campaign.previewImageUrl | templatePreviewImagePlaceholder"/>
                                </div>
                                <div class="label-container">
                                    <div class="version-container">
                                        <div class="version-title">
                                            <p class="red-text version-heading">Version A</p>
                                            <div v-if="campaign.testType === 'Subject'" class="accent-color">"{{ campaign.subjectA }}"</div>
                                        </div>
                                        <div class="details">
                                            <div class="split-percentage">{{campaign.testedContactsPercentage / 2 }}<span class="percent">%</span></div>
                                        </div>
                                    </div>
                                    <div class="progress-container">
                                        <div class="progress-fill red-gradient-bg" :style="{width:campaign.progressBarAWidth}"></div>
                                    </div>
                                    <span class="fraction">{{ campaign.splitTestedContacts + campaign.oddSplitCorrection | commaDelimited}} Recipients of {{ campaign.uniqueContactCount | commaDelimited}} Total Recipients</span>
                                </div>
                            </div>

                            <div class="test-content">
                                <k16-b-test-svg />
                                <div class="preview" 
                                    @click="previewContent(campaign,'B')"
                                    v-if="campaign.testType === 'Content'">
                                    <div class="preview-overlay"><eye-svg /></div>
                                    <img :src="campaign.previewImageUrl | templatePreviewImagePlaceholder" />
                                </div>
                                <div class="label-container">
                                    <div class="version-container">
                                        <div class="version-title">
                                            <div class="blue-text version-heading">Version B</div>
                                            <div v-if="campaign.testType === 'Subject'" class="accent-color">"{{ campaign.subjectB }}"</div>
                                        </div>
                                        <div class="details">
                                            <div class="split-percentage">{{campaign.testedContactsPercentage / 2 }}<span class="percent">%</span></div>
                                        </div>
                                    </div>
                                    <div class="progress-container">
                                        <div class="progress-fill blue-gradient-bg" :style="{width:campaign.progressBarBWidth}"></div>
                                    </div>
                                    <span class="fraction" >{{ campaign.splitTestedContacts + campaign.oddSplitCorrection | commaDelimited}} Recipients of {{ campaign.uniqueContactCount | commaDelimited}} Total Recipients</span>
                                </div>
                            </div>

                            <div class="test-content version-winner">
                                <div class="green-text version-heading">Winner Sent To</div>
                                <div class="details">
                                    <div class="accent-color">Based on version with highest <span v-if="campaign.testMetric === 'Open'">open</span><span v-else>click-through</span> rate</div>
                                    <div class="split-percentage">{{100 - campaign.testedContactsPercentage}}<span class="percent">%</span></div>
                                </div>
                                <div class="progress-container">
                                    <div class="progress-fill green-gradient-bg" :style="{width:campaign.progressBarCWidth}"></div>
                                </div>
                                <span class="fraction">{{ campaign.uniqueContactCount - campaign.testedContacts | commaDelimited}} Recipients of {{ campaign.uniqueContactCount | commaDelimited}} Total Recipients</span>
                                
                            </div>
                        </div>
                        <div class="sent-row" v-if="campaign.isSent">
                            <div class='opens'>
                                <div class="pie-chart">
                                    <compressed-two-donut-value
                                        value1Color="grey"
                                        value2Color="green"
                                        description="Open"
                                        :percent="campaign.openRate">
                                    </compressed-two-donut-value>
                                </div>
                                <div class="totals">
                                    <div class="pie-chart-key"><div class="color-key"></div><span class="heavy-weight">Unopened</span> {{campaign.unopened | commaDelimited}} </div>
                                    <div class="pie-chart-key"><div class="color-key green"></div><span class="heavy-weight">Opens</span> {{campaign.uniqueOpens | commaDelimited}}</div>
                                </div>
                            </div>
                            <div class="clicks">
                                <div class="pie-chart">
                                    <compressed-two-donut-value
                                        value1Color="green"
                                        value2Color="blue"
                                        description="Clicks"
                                        :percent="campaign.clickRate">
                                    </compressed-two-donut-value>
                                </div>
                                <div class="totals">
                                    <div class="pie-chart-key"><div class="color-key green"></div><span class="heavy-weight">Opens</span> {{campaign.uniqueOpens | commaDelimited}}</div>
                                    <div class="pie-chart-key"><div class="color-key blue"></div><span class="heavy-weight">Clicks</span> {{campaign.totalClicks | commaDelimited}}</div>
                                </div>
                            </div>
                        </div>
                        <div class="overview-row" v-if="campaign.isSent">
                            <div class="schedule-date">
                                <k3-email-stroke-svg />
                                <p>Sent {{campaign.scheduledSendDate | momentDate}}</p>
                            </div>
                            <app-button @click="viewCampaignOverview(campaign)">Campaign Overview <s7-export-svg /></app-button>
                        </div>
                        <div class="unschedule-row" v-if="campaign.isScheduled && !campaign.isSent">
                            <div class="unschedule-date">
                                <k3-email-stroke-svg />
                                <p>{{campaign.scheduledSendDate | momentDate}}</p>
                            </div>
                            <app-button class="unschedule-button" @click="unscheduleCampaign(campaign)">
                                Unschedule 
                                <k9-unschedule-marketing-plan-svg />
                            </app-button>
                        </div>
                        <div class="schedule-row" v-if="!campaign.isScheduled">
                            <div class="schedule-date">
                                <k9-unschedule-marketing-plan-svg />
                                <p>{{campaign.scheduledSendDate | momentDate}}</p>
                            </div>
                            <app-button @click="showSchedulePopup(campaign , day)">
                                Schedule 
                                <l9-schedule-marketing-plan-svg />
                            </app-button>
                        </div>
                        <div class="schedule-warning" v-if="!campaign.isScheduled && !campaign.isSent">
                            <t3-alert-svg />
                            <p>This campaign won't be sent until you schedule it</p>
                            <t3-alert-svg />
                        </div>
                    </div>
                </div>
                <div v-else class="no-campaigns-message">
                    There are currently no campaigns scheduled for this day.
                </div>

                <div class="create-campaign-container" v-if="!isBeforeToday">
                    <app-button @click="openCreateCampaignWidget()"><plus-icon-svg /></app-button>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="cancel-btn" @click="closeWidget">
                        Cancel
                    </app-button>
                </div>
            </div>
        </div>
    </div>
</template>
 
<script>
const AppButton = () => import('@/components/common/app-button');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const InfoIconSvg = () => import('@/components/svg/grey-info-icon-svg');
const J9OverviewSvg  = () => import('@/components/svg/grid/j9-overview-svg');
const K9UnscheduleMarketingPlanSvg = () => import('@/components/svg/grid/k9-unschedule-marketing-plan-svg');
const L9ScheduleMarketingPlanSvg = () => import('@/components/svg/grid/l9-schedule-marketing-plan-svg');
const EyeSvg = () => import('@/components/svg/eye-svg');
const T3AlertSvg = () => import('@/components/svg/grid/t3-alert-svg');
const CompressedTwoDonutValue = () => import('@/components/common/compressed-two-donut-value');
const K3EmailStroke = () => import('@/components/svg/grid/k3-email-stroke-svg');
const S7ExportSvg = () => import('@/components/svg/grid/s7-export-svg');
const R7EditSvg = () => import('@/components/svg/grid/r7-edit-svg');
const K16BTestSvg = () => import('@/components/svg/grid/k16-b-test-svg');
const J16ATestSvg = () => import('@/components/svg/grid/j16-a-test-svg');
const S4DeleteSvg = () => import('@/components/svg/grid/s4-delete-svg');
const PlusIconSvg  = () => import("@/components/svg/plus-icon-svg");

import AppPopup from '@/components/popups/app-popup';
import EventBus from '@/scripts/event-bus';
import { LogLevel } from "@/scripts/logger";

export default {
    name: 'marketing-plan-day-overview-widget',

    props: {
        widget: {
            type: Object,
            required: true
        }
    },

    components: {
        'app-button': AppButton,
        'widget-close-svg': WidgetCloseSvg,
        'loading-screen-svg': LoadingScreenSvg,
        'j9-overview-svg': J9OverviewSvg,
        'k9-unschedule-marketing-plan-svg': K9UnscheduleMarketingPlanSvg,
        'l9-schedule-marketing-plan-svg': L9ScheduleMarketingPlanSvg,
        'grey-info-icon-svg': InfoIconSvg,
        'eye-svg': EyeSvg,
        't3-alert-svg': T3AlertSvg,
        'compressed-two-donut-value': CompressedTwoDonutValue,
        'k3-email-stroke-svg': K3EmailStroke,
        's7-export-svg': S7ExportSvg,
        'r7-edit-svg': R7EditSvg,
        'k16-b-test-svg': K16BTestSvg,
        'j16-a-test-svg': J16ATestSvg,
        's4-delete-svg': S4DeleteSvg,
        'plus-icon-svg': PlusIconSvg,
    },

    data() {
        return {
            isLoading: true,
            campaigns: [],
            day: this.widget.day.local().startOf('day'),
            today: this.$moment().local().startOf('day'),
        }
    },

    computed: {
        isBeforeToday() {
            return this.day.isBefore(this.today);
        },

        hasCampaignsScheduled() {
            return this.campaigns.length > 0;
        },
    },

    methods: {
         executeQuery() {
            return new Promise((resolve, reject) => {
                var startDate = this.day.local().startOf('day').utc();
                var endDate = startDate.clone().add(1, 'days');
                var formattedStartDate = startDate.format('YYYY-MM-DDTHH:mm:ss') + 'z';
                var formattedEndDate = endDate.format('YYYY-MM-DDTHH:mm:ss') + 'z';
                
                var filter = `$filter=ClientId eq ${ this.widget.clientId } and ((ScheduledSendDate ge ${ formattedStartDate } and ScheduledSendDate lt ${ formattedEndDate }) or (DateSent ge ${ formattedStartDate } and DateSent lt ${ formattedEndDate } and ScheduledSendDate eq null))`;
                var select = `$select=Id,Name,SubjectA,SubjectB,PreviewImageUrl,ScheduledSendDate,OpenRate,ClickRate,TotalClicks,TotalOpens,IsScheduled,DateSent,TotalRecipients,HasAbTest,TestType,TestPercent,TestMetric,Timezone,IsPlain,WinningVersion,TotalBounces,TotalUniqueOpens,TotalClicks`;

                var query = `/service/Campaigns?${filter}&${select}`;

                this.$api
                    .get(query)
                    .then(response => {
                        var dataResponse = response.data.value;
                        
                        this.campaigns = dataResponse.map(dataResponseItem => {

                            var localScheduleDate = dataResponseItem.ScheduledSendDate ? this.$moment.utc(dataResponseItem.ScheduledSendDate).local() : this.$moment.utc(dataResponseItem.DateSent).local();
                            
                            return {
                                id: dataResponseItem.Id,
                                name: dataResponseItem.Name,
                                subjectA: dataResponseItem.SubjectA,
                                subjectB: dataResponseItem.SubjectB,
                                previewImageUrl: dataResponseItem.PreviewImageUrl,
                                isPlain: dataResponseItem.IsPlain,
                                openRate: dataResponseItem.OpenRate,
                                clickRate: dataResponseItem.ClickRate,
                                scheduledSendDate: localScheduleDate,
                                totalClicks: dataResponseItem.TotalClicks | 0,
                                uniqueOpens: dataResponseItem.TotalUniqueOpens | 0,
                                isScheduled: dataResponseItem.IsScheduled,
                                isSent: dataResponseItem.DateSent ? true : false,
                                totalRecipients: dataResponseItem.TotalRecipients,
                                hasAbTest: dataResponseItem.HasAbTest,
                                winningVersion: dataResponseItem.WinningVersion,
                                testType: dataResponseItem.TestType,
                                testedContactsPercentage: dataResponseItem.TestPercent,
                                uniqueContactCount: dataResponseItem.TotalRecipients,
                                unopened: (dataResponseItem.TotalRecipients - dataResponseItem.TotalBounces - dataResponseItem.TotalUniqueOpens),
                                testMetric: dataResponseItem.TestMetric,
                                progressBarAWidth: dataResponseItem.TestPercent ? dataResponseItem.TestPercent / 2 + '%' : null,
                                progressBarBWidth: dataResponseItem.TestPercent ? dataResponseItem.TestPercent / 2 + '%' : null,
                                progressBarCWidth: dataResponseItem.TestPercent ? 100 - dataResponseItem.TestPercent + '%' : null,
                                testedContacts: Math.floor(dataResponseItem.TotalRecipients * dataResponseItem.TestPercent / 100),
                                splitTestedContacts: Math.floor(dataResponseItem.TotalRecipients * dataResponseItem.TestPercent / 100) / 2,
                                oddSplitCorrection: Math.floor(dataResponseItem.TotalRecipients * dataResponseItem.TestPercent / 100) % 2 == 1 ? 1 : 0,
                            };

                        });
                        resolve();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification('Failed to get Marketing Plan.', { error });
                    })
            });
        }, 

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        viewCampaignOverview(campaign) {
            this.$router.push(`/Campaigns/${ campaign.id }`);
            this.closeWidget();
        },

        previewContent(campaign, version) {
            var campaignVersion = version || 'A';
            window.open(`/Campaigns/${ campaign.id }/preview?version=${ campaignVersion }`, '_blank');
        },

        openEditCampaignWidget(campaign) {
            EventBus.$emit('open-widget', { 
                name: 'campaign-widget',
                campaignId: campaign.id
            })
        },

        unscheduleCampaign(campaign) {
            return new Promise((resolve, reject) => {
                var changes = [{'name': 'IsScheduled','value': false}];
                
                this.$api
                    .patch(`/api/Campaigns/${ campaign.id }`, changes)
                    .then(() => {
                        campaign.isScheduled = false;
                        EventBus.$emit('campaign:update');
                        resolve();
                    })
                    .catch(response => {
                        reject();
                    })    

                });
        },

        showSchedulePopup(campaign, day) {
            var content = "Are you sure you want to schedule this campaign?"
            var confirmText = "Schedule"

            if( campaign.scheduledSendDate.isBefore(this.today)) {
                content = "This campaign is scheduled in the past. This campaign will be sent out now, if scheduled."
                confirmText = "Send Now"
            }

            this.actions = {
                confirm: {
                    label: confirmText,
                    action: () => { this.scheduleCampaign(campaign); }
                },
                cancel: {
                    label: 'Cancel'
                }
            };

            this.$modal.show(AppPopup, {
                type: 'alert-popup',
                title: `Schedule`,
                content: content,
                actions: this.actions,
                confirmText: confirmText
            },{
                height: this.$helpers.setPopupHeight(),
                width: this.$helpers.setPopupWidth()
            });
        },

        scheduleCampaign(campaign) {
            return new Promise((resolve, reject) => {
                var changes = [{'name': 'IsScheduled','value': true}];

                this.$api
                    .patch(`/api/Campaigns/${ campaign.id }`, changes)
                    .then(() => {
                        campaign.isScheduled = true;
                        EventBus.$emit('campaign:update');
                        resolve();
                    })
                    .catch(response => {
                        reject();
                    })    
            });
        },

        showCampaignDeletePopUp(campaign) {
            this.actions =  {
                confirm: {
                    label: `Delete Campaign`,
                    action: () => {  this.deleteCampaign(campaign); }
                },

                cancel: {
                    label: 'Cancel'
                }
            };

            this.$modal.show(AppPopup, {
                type: 'alert-popup',
                title: `Are you sure?`,
                content: `<p>All information associated with this campaign will be deleted. You can not undo this action.</p>`,
                actions: this.actions,
                confirmText: 'Delete'
            },{
                height: this.$helpers.setPopupHeight(),
                width: this.$helpers.setPopupWidth()
            });
        },

        deleteCampaign(campaign) {
            this.isLoading = true;
            this.$api
                .delete(`/api/Campaigns/${ campaign.id }`)
                .then(() => {
                    this.$helpers.showSuccessNotification(`Your campaign has been deleted.`);
                    EventBus.$emit('campaign:update');
                    this.executeQuery();
                    this.isLoading = false;
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('An error occurred while deleting your campaign.', { error });
                });
        },

        openCreateCampaignWidget() {
            if(this.day.isBefore(this.$moment().startOf('day'))) {
                this.$helpers.showFailNotification('Unable to create campaigns in the past.', { level: LogLevel.INFO });
            }
            else {
                var sendDate = this.$moment(this.day).set({ hour: 10, minute: 0, second: 0, millisecond: 0 });

                this.closeWidget();

                EventBus.$emit('open-widget', { 
                    name: 'campaign-widget',
                    clientId: this.widget.clientId,                    
                    sendDate: sendDate
                })
            }            
        }
    },

    created() {
        EventBus.$on('campaign:update', this.executeQuery);
        this.executeQuery()
        .then(() => {
            this.isLoading = false;
        })
    },

    beforeDestroy() {
        EventBus.$off('campaign:update', this.executeQuery);
    }
}
</script>

<style>
.marketing-plan-day-overview-widget .no-campaigns-message {
    text-align: center;
    margin-bottom: 30px;
}

.marketing-plan-day-overview-widget .campaign {
    margin-bottom: 40px;
    padding: 14px 23px 23px;
    position: relative;
}

.marketing-plan-day-overview-widget .heavy-weight {
    font-weight: 600;
}

.marketing-plan-day-overview-widget .cancel-btn {
    background: transparent;
    color: #877D88;
    border: none;
}

.marketing-plan-day-overview-widget .cancel-btn:hover {
    background: #E9E9EF;
}

.marketing-plan-day-overview-widget .widget-title-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.marketing-plan-day-overview-widget .j9-overview-svg {
    position: relative;
    margin-right: 28px;
}

.marketing-plan-day-overview-widget .campaign-name {
    font-size: 16px;
    color: #382153;
    font-weight: 600;
    margin-bottom: 5px;
    line-height: 1em;
}

.marketing-plan-day-overview-widget .campaign-name.unsent-campaign {
    color: #18AEFF;
    cursor: pointer;
    display: flex;
    align-items: center;
}

.marketing-plan-day-overview-widget .campaign-subject {
    font-size: 14px;
    margin-bottom: 0px;
}

.marketing-plan-day-overview-widget .preview {
    width: 160px;
    height: 165px;
    overflow: hidden;
    border-radius: 18px;
}

.marketing-plan-day-overview-widget  .preview {
    position: relative;
    cursor: pointer;
}
.marketing-plan-day-overview-widget  .preview-overlay {
    position: absolute;
    display: -webkit-box;  /* OLD - iOS 6-, Safari 3.1-6, BB7 */
    display: -ms-flexbox;  /* TWEENER - IE 10 */
    display: -webkit-flex; /* NEW - Safari 6.1+. iOS 7.1+, BB10 */
    display: flex;         /* NEW, Spec - Firefox, Chrome, Opera */
    align-items: center;
    justify-content: center;

    z-index: 1;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    height: 100%;
    width: 100%;
    opacity: 0;
    background-color: rgba(89, 72, 110, 1);

    -webkit-transition: opacity 500ms ease-out;
    -moz-transition: opacity 500ms ease-out;
    -o-transition: opacity 500ms ease-out;
    transition: opacity 500ms ease-out;
}
.marketing-plan-day-overview-widget .title-row {
    display: flex;
    align-items: center;
    margin-bottom: 16px;
}

.marketing-plan-day-overview-widget .campaign-name-container {
    display: flex;
    flex-direction: column;
}

.marketing-plan-day-overview-widget .preview {
    width: 93px;
    height: 63px;
    margin-right: 13px;
}

.marketing-plan-day-overview-widget .preview-overlay:hover {
    opacity: 0.8;
}

.marketing-plan-day-overview-widget .preview-overlay .preview-text {
    position: relative;
    font-size: 26px;
    font-weight: 600;
    color: #fff;
    z-index: 1;
}

.marketing-plan-day-overview-widget .eye-svg {
    width: 22.77px;
    top: 10px;
    right: 15px;
}

.marketing-plan-day-overview-widget .eye-svg path,
.marketing-plan-day-overview-widget .eye-svg circle{
    fill: #fff;
}

.marketing-plan-day-overview-widget .schedule-row,
.marketing-plan-day-overview-widget .unschedule-row,
.marketing-plan-day-overview-widget .overview-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 20px;
    margin-bottom: 17px;
    padding: 0;
}

.marketing-plan-day-overview-widget .schedule-date,
.marketing-plan-day-overview-widget .unschedule-date {
    display: flex;
    align-items: center;
    max-width: 50%;
    width: 100%;
}

.marketing-plan-day-overview-widget .schedule-row p,
.marketing-plan-day-overview-widget .unschedule-row p,
.marketing-plan-day-overview-widget .overview-row p {
    margin-bottom: 0;
    line-height: 1em;
    font-size: 12px;
}

.marketing-plan-day-overview-widget .k9-unschedule-marketing-plan-svg {
    width: 24px;
    height: 34px;
}

.marketing-plan-day-overview-widget .app-button .s7-export-svg {
    width: 16px;
    height: 16px;
}

.marketing-plan-day-overview-widget .k9-unschedule-marketing-plan-svg path {
    fill: #F30070;
}

.marketing-plan-day-overview-widget .k9-unschedule-marketing-plan-svg line {
    stroke: #F30070;
}

.marketing-plan-day-overview-widget .l9-schedule-marketing-plan-svg,
.marketing-plan-day-overview-widget .s7-export-svg {
    margin-left: 17px; 
}

.marketing-plan-day-overview-widget .k9-unschedule-marketing-plan-svg {
    margin-right: 17px;
}

.marketing-plan-day-overview-widget .unschedule-button .k9-unschedule-marketing-plan-svg {
    margin-right: 0px;
    margin-left: 17px
}

.marketing-plan-day-overview-widget .l9-schedule-marketing-plan-svg path,
.marketing-plan-day-overview-widget .s7-export-svg path,
.marketing-plan-day-overview-widget .s7-export-svg rect {
    fill: #fff
}

.marketing-plan-day-overview-widget .k3-email-stroke-svg {
    max-width: 43px;
    margin-right: 12px;
    width: 100%;
    height: auto;
}

.marketing-plan-day-overview-widget .schedule-warning {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #F3007017;
    padding: 6px 14px;
    border-radius: 19px;
}

.marketing-plan-day-overview-widget .schedule-warning p {
    margin-bottom: 0px;
    font-size: 11px;
    line-height: 1em;
    color: #F30070;
}

.marketing-plan-day-overview-widget .sent-row {
    display: flex;
    margin-top: 12px;
    padding: 0 10px;
}

.marketing-plan-day-overview-widget .clicks,
.marketing-plan-day-overview-widget .opens {
    display: flex;
    width: 50%;
}

.marketing-plan-day-overview-widget .totals {
    display: flex;
    flex-direction: column;
    justify-content: center;
    margin-top: 10px;
}

.marketing-plan-day-overview-widget .totals .heavy-weight {
    margin-right: 10px; 
}

.marketing-plan-day-overview-widget .totals p {
    font-size: 12px;
    margin-bottom: 0px;
}

.marketing-plan-day-overview-widget .pie-chart .module {
    background: transparent;
    box-shadow: none;
}

.marketing-plan-day-overview-widget .pie-chart .module .content {
    padding-left: 0px;
    padding-bottom: 0px;
}

.marketing-plan-day-overview-widget .color-key {
    width: 15px;
    height: 15px;
    border-radius: 50%;
    background: #9996ad;
    margin-right: 10px;
}

.marketing-plan-day-overview-widget .pie-chart-key {
    display: flex;
    font-size: 12px;
    margin-bottom: 5px;
}

.marketing-plan-day-overview-widget .pie-chart-key .green {
    background: #28da35;
}

.marketing-plan-day-overview-widget .pie-chart-key .blue {
    background: #1fa2ff;
}

.marketing-plan-day-overview-widget .r7-edit-svg path {
    fill: #18AEFF;
}

.marketing-plan-day-overview-widget .r7-edit-svg {
    margin-left: 10px;
    position: relative;
    bottom: 3px;
}

.marketing-plan-day-overview-widget .campaign:nth-child(even) {
    background: #F7F7F8;
    border-radius: 15px;
}

.marketing-plan-day-overview-widget .test-content {
    display: flex;
    margin-bottom: 16px;
}

.marketing-plan-day-overview-widget .progress-container {
    position: relative;
    overflow: hidden;
    height: 20px;
    border-radius: 10px;
    background-color: #E9E9EF;
}

/*Unknown Schema Requirement*/
.marketing-plan-day-overview-widget .progress-fill {
    background: #f30070; 
    background: -moz-linear-gradient(left,  #f30070 1%, #eb7e0a 100%);
    background: -webkit-linear-gradient(left,  #f30070 1%,#eb7e0a 100%);
    background: linear-gradient(to right,  #f30070 1%,#eb7e0a 100%);
    filter: progid:DXImageTransform.Microsoft.gradient( startColorstr='#f30070', endColorstr='#eb7e0a',GradientType=1 );
    width: 0;
    height: 10px;
    border-radius: 20px;
    transition: width 750ms ease-out 250ms;
}

.marketing-plan-day-overview-widget .j16-a-test-svg,
.marketing-plan-day-overview-widget .k16-b-test-svg {
    margin-right: 17px;
}

.marketing-plan-day-overview-widget .progress-container {
    position: relative;
    overflow: hidden;
    height: 10px;
    border-radius: 10px;
    width: 423px;
    background-color: #E9E9EF;
}

.marketing-plan-day-overview-widget .subject-test .progress-container {
    width: 530px;
}

.marketing-plan-day-overview-widget .fraction {
    font-size: 11px;
}

.marketing-plan-day-overview-widget .version-heading {
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 0px;
}

.marketing-plan-day-overview-widget .red-text {
    color: #F30070
}

.marketing-plan-day-overview-widget .blue-text {
    color: #18AEFF
}

.marketing-plan-day-overview-widget .green-text {
    color: #2ADA2B
}

.marketing-plan-day-overview-widget .version-container,
.marketing-plan-day-overview-widget .details {
    display: flex;
    justify-content: space-between;
}

.marketing-plan-day-overview-widget .details {
    align-items: baseline;
    margin-top: -10px;
}

.marketing-plan-day-overview-widget .split-percentage {
    font-size: 20px;
    color: #382153;
    font-weight: 600;
}

.marketing-plan-day-overview-widget .version-winner {
    padding-left: 157px;
    display: block;
}

.marketing-plan-day-overview-widget .subject-test .version-winner {
    padding-left: 45px;
}

.marketing-plan-day-overview-widget .accent-color {
    font-size: 12px;
}

.marketing-plan-day-overview-widget .unschedule-button {
    background: transparent;
    color: #F30070;
    border: 2px solid #F30070;
}

.marketing-plan-day-overview-widget .unschedule-button:hover {
    background: #F30070;
    color: #fff;    
}

.marketing-plan-day-overview-widget .unschedule-button:hover .k9-unschedule-marketing-plan-svg path {
    fill: #fff
}

.marketing-plan-day-overview-widget .unschedule-button:hover .k9-unschedule-marketing-plan-svg line {
    stroke: #fff
}

.marketing-plan-day-overview-widget .campaigns .delete-btn {
    background: #F30070;
    padding: 0px 8px;
    height: 31px;
    position: absolute;
    top: 14px;
    right: 14px;
    cursor: pointer;
}

.marketing-plan-day-overview-widget .campaigns .delete-btn:hover {
    background: #c80768;
}

.marketing-plan-day-overview-widget .delete-btn svg {
    width: 14px;
    height: auto;    
}

.marketing-plan-day-overview-widget .delete-btn path,
.marketing-plan-day-overview-widget .delete-btn rect {
    fill: #fff;
}

.marketing-plan-day-overview-widget .hovering .create-campaign-container {
    display: flex;
}

.marketing-plan-day-overview-widget .create-campaign-container button.app-button.normal {
    display: block;
    width: 100%;
    height: 39px;
    display: flex;
    align-items: center;
    cursor: pointer;
}

.marketing-plan-day-overview-widget .create-campaign-container button svg path {
    fill: #fff
}

.marketing-plan-day-overview-widget .create-campaign-container button svg {
    width: 20px;
    height: 20px;
}

.marketing-plan-day-overview-widget .preview {
    position: relative;
}

.marketing-plan-day-overview-widget .version-bubble {
    position: absolute;
    bottom: -2px;
    right: 0;
    width: 35px;
    height: 35px;
}

.marketing-plan-day-overview-widget .version-bubble svg {
    border: 3px solid #fff;
    border-radius: 50%;
}

.marketing-plan-day-overview-widget .version-winner-text {
    font-size: 11px;
}

.marketing-plan-day-overview-widget .pie-chart svg .highcharts-background {
    fill: transparent !important;
}
</style>